/**
 * Product Card Color Swatch Handler
 * Handles image switching when clicking color swatches on product cards
 */
(function() {
if (customElements.get('product-card-swatches')) return;

// Nosto injects PLP cards after Rivo's one-time scan, so the heart renders
// but has no click handler until we ask Rivo to bind new buttons.
let rivoFavoriteBindQueued = false;
let rivoFavoriteBindWaiting = false;

function bindRivoFavoriteButtons() {
  const rivoRoot = document.getElementById('rivo-account-slideout');
  const data = window.RivoProfileAlpine?.$data?.(rivoRoot);
  if (typeof data?.initializeFavoriteButtons !== 'function') return false;
  data.initializeFavoriteButtons();
  return true;
}

function queueRivoFavoriteBind() {
  if (rivoFavoriteBindQueued) return;
  rivoFavoriteBindQueued = true;
  queueMicrotask(() => {
    rivoFavoriteBindQueued = false;
    if (bindRivoFavoriteButtons() || rivoFavoriteBindWaiting) return;
    rivoFavoriteBindWaiting = true;
    document.addEventListener('rivo-accounts:loaded', () => {
      rivoFavoriteBindWaiting = false;
      bindRivoFavoriteButtons();
    }, { once: true });
  });
}

class ProductCardSwatches extends HTMLElement {
  static PLP_IMAGE_WIDTHS = [400, 800, 1200, 1600, 2000, 2400];
  static PLP_IMAGE_SIZES = '(min-width: 1536px) 33vw, (min-width: 1024px) 40vw, 55vw';
  // Hosts allowed to appear in an image src/srcset we build from DOM data attributes.
  // The page's own origin is always allowed in addition to these.
  static ALLOWED_IMAGE_HOSTS = /(^|\.)(shopify\.com|shopifycdn\.com|shopifycdn\.net|myshopify\.com)$/i;

  constructor() {
    super();
  }

  /**
   * Validates a URL read from a DOM data attribute before it is used as an image
   * source, and returns a normalized absolute URL. Returns '' for anything that is
   * not an http(s) image on the current origin or an approved Shopify CDN host, so
   * untrusted DOM text can never reach an element's src/srcset.
   */
  sanitizeImageUrl(url) {
    if (!url || typeof url !== 'string') return '';

    let parsed;
    try {
      parsed = new URL(url.trim(), window.location.href);
    } catch (error) {
      return '';
    }

    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '';

    const host = parsed.hostname.toLowerCase();
    if (host !== window.location.hostname.toLowerCase() && !ProductCardSwatches.ALLOWED_IMAGE_HOSTS.test(host)) {
      return '';
    }

    return parsed.href;
  }

  withImageWidth(url, width) {
    if (!url) return '';
    if (/[?&]width=\d+/i.test(url)) {
      return url.replace(/([?&])width=\d+/i, `$1width=${width}`);
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}`;
  }

  buildPlpSrcset(url) {
    return ProductCardSwatches.PLP_IMAGE_WIDTHS
      .map((width) => `${this.withImageWidth(url, width)} ${width}w`)
      .join(', ');
  }

  applyResponsiveImage(img, url) {
    if (!img) return;

    if (!url) {
      img.removeAttribute('src');
      img.removeAttribute('srcset');
      return;
    }

    // Only ever hand a validated, self-constructed URL to src/srcset.
    // If validation fails, leave the existing image in place rather than blanking the card.
    const safeUrl = this.sanitizeImageUrl(url);
    if (!safeUrl) return;

    const sizes = img.getAttribute('sizes') || ProductCardSwatches.PLP_IMAGE_SIZES;
    img.setAttribute('src', this.withImageWidth(safeUrl, 1200));
    img.setAttribute('srcset', this.buildPlpSrcset(safeUrl));
    img.setAttribute('sizes', sizes);
  }

  connectedCallback() {
    this.swatches = this.querySelectorAll('[data-product-card-swatch]');
    this.mainImage = this.querySelector('[data-product-main-image]');
    this.secondaryImage = this.querySelector('[data-product-secondary-image]');
    this.productLink = this.querySelector('.product-card--images');
    this.price = this.querySelector('[data-product-price]');
    this.comparePrice = this.querySelector('[data-product-compare-price]');
    this.wishListButton = this.querySelector('[data-product-wishlist-button]');
    this.promotionalLinesJson = this.parsePromotionalLines();

    this.limitSwatches();
    this.init();
    this.syncWithColorFilter();
    queueRivoFavoriteBind();

    // Non-blocking: fetch real inventory and apply safety-stock sold-out states
    this.fetchAndApplySafetyStock();

    this._boundLimitSwatches = () => this.limitSwatches();
    window.addEventListener('resize', this._boundLimitSwatches);

    this._boundSyncWithColorFilter = () => this.syncWithColorFilter();
    document.addEventListener('collection-content-updated', this._boundSyncWithColorFilter);
  }

  disconnectedCallback() {
    if (this._boundLimitSwatches) {
      window.removeEventListener('resize', this._boundLimitSwatches);
    }
    if (this._boundSyncWithColorFilter) {
      document.removeEventListener('collection-content-updated', this._boundSyncWithColorFilter);
    }
  }

  updateSectionFromSwatch({
    variantId,
    sectionApi,
    selector,
    el,
    errorMsg
  }) {
    const activeSwatch = this.querySelector('[data-product-card-swatch].active');
    if (!activeSwatch || !el) return;
    const productHandle = this.getAttribute('data-product-handle');
    if (!variantId || !productHandle) return;
    const url = `/products/${productHandle}`;
    const requestUrl = `${url}?variant=${variantId}&sections[]=${sectionApi}`;
    fetch(requestUrl)
      .then((response) => response.json())
      .then((data) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data[sectionApi], 'text/html');
        const content = doc.querySelector(selector);
        if (content && content.innerHTML.trim() !== '') {
          el.innerHTML = content.innerHTML;
          el.classList.remove('hidden');
        } else {
          el.innerHTML = '';
          el.classList.add('hidden');
        }
      })
      .catch((error) => {
        console.error(errorMsg, error);
        el.innerHTML = '';
        el.classList.add('hidden');
      });
  }

  parsePromotionalLines() {
    try {
      const raw = this.getAttribute('data-promotional-lines');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  updatePromotionalLineFromSwatch(variantId) {
    const el = this.querySelector('[data-promotional-line]');
    if (!el) return;

    const lines = this.promotionalLinesJson || this.parsePromotionalLines();
    const variantIdNum = Number.parseInt(String(variantId), 10);
    const variantData = Number.isFinite(variantIdNum)
      ? lines.find((variant) => Number(variant.id) === variantIdNum)
      : null;
    const html = variantData?.promotional_line_html || '';
    const nextVariantId = variantId ? String(variantId) : '';

    if (el.dataset.variantId === nextVariantId) return;

    const parsed = new DOMParser().parseFromString(typeof html === 'string' ? html : '', 'text/html');
    el.replaceChildren(...parsed.body.childNodes);
    const color = typeof variantData?.promotional_line_color === 'string'
      ? variantData.promotional_line_color
      : '';
    el.style.color = color;
    el.dataset.variantId = nextVariantId;
  }

  updateBadgesFromSwatch(variantId) {
    this.updateSectionFromSwatch({
      variantId,
      sectionApi: 'badges-section-api',
      selector: '[data-badge]',
      el: this.querySelector('[data-badge]'),
      errorMsg: 'Error fetching product badges:'
    });
  }

  limitSwatches(prioritySwatch) {
    const container = this.querySelector('.product-card__swatches') || this.closest('.product-card__swatches');
    if (!container) return;

    const swatchLimit = window.innerWidth < 768 ? 4 : 6;
    const swatchNodes = Array.from(container.querySelectorAll('[data-product-card-swatch]'));

    // When filtering by color: if the matching swatch is in the hidden range (5+ mobile, 7+ desktop), move it to first so CSS shows it
    if (prioritySwatch && swatchNodes.includes(prioritySwatch)) {
      const index = swatchNodes.indexOf(prioritySwatch);
      if (index >= swatchLimit) {
        container.insertBefore(prioritySwatch, container.firstChild);
        this.swatches = this.querySelectorAll('[data-product-card-swatch]');
      }
    }

    // Overflow indicator: "+ N" when total > limit (visibility is controlled by CSS nth-child)
    const overflow = this.querySelector('[data-remaining-swatches]');
    if (overflow) {
      const totalFromServer = overflow.getAttribute('data-total-swatches');
      const total = totalFromServer !== null && totalFromServer !== ''
        ? parseInt(totalFromServer, 10)
        : this.swatches.length;
      const remaining = Math.max(0, total - swatchLimit);

      overflow.textContent = remaining > 0 ? '+ ' + remaining : '';
      overflow.setAttribute('data-remaining-swatches', String(remaining));
      overflow.classList.toggle('hidden', remaining <= 0);
    }
  }

  syncWithColorFilter() {
    const params = new URLSearchParams(window.location.search);
    const activeColors = [];
    for (const [key, value] of params) {
      if (key.toLowerCase().includes('color') && value) {
        activeColors.push((value || '').trim());
      }
    }

    if (activeColors.length === 0) return;

    const container = this.querySelector('.product-card__swatches') || this.closest('.product-card__swatches');
    if (!container) return;

    const allSwatches = Array.from(container.querySelectorAll('[data-product-card-swatch]'));

    const swatchMatchesFilter = (swatch, filterValue) => {
      const filterLower = filterValue.toLowerCase().trim();
      const filterHandle = filterLower.replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const color = (swatch.getAttribute('data-swatch-color') || '').trim();
      const handle = (swatch.getAttribute('data-swatch-color-handle') || '').trim();
      const colorLower = color.toLowerCase();
      const title = (swatch.getAttribute('title') || '').trim().toLowerCase();
      return (
        (colorLower && (colorLower === filterLower || colorLower.includes(filterLower) || filterLower.includes(colorLower))) ||
        (handle && (handle === filterHandle || handle.includes(filterHandle) || filterHandle.includes(handle))) ||
        (title && (title === filterLower || title.includes(filterLower) || filterLower.includes(title)))
      );
    };

    // Sort filter values by specificity (longest first) so "Light Pink" is tried before "Black"
    const sortedColors = [...activeColors].sort((a, b) => (b.length - a.length));

    let matchingSwatch = null;
    for (const filterValue of sortedColors) {
      const found = allSwatches.find((swatch) => swatchMatchesFilter(swatch, filterValue));
      if (found) {
        matchingSwatch = found;
        break;
      }
    }

    if (matchingSwatch) {
      this.swatches = this.querySelectorAll('[data-product-card-swatch]');
      this.limitSwatches(matchingSwatch);
      this.applySwatchVariant(matchingSwatch);
    } else {
      this.limitSwatches();
    }
  }

  /**
   * Applies variant data from a swatch to the card (image, price, link, badges, etc.).
   * Used when syncing with color filter so the displayed content matches the active swatch.
   */
  applySwatchVariant(swatch) {
    if (!swatch) return;

    const variantComparePrice = swatch.dataset.variantComparePrice || swatch.getAttribute('data-variant-compare-price');
    const variantPrice = swatch.dataset.variantPrice || swatch.getAttribute('data-variant-price');
    const variantId = swatch.dataset.variantId || swatch.getAttribute('data-variant-id');
    const productId = swatch.dataset.productId || swatch.getAttribute('data-product-id');
    const mainImageUrl = swatch.dataset.mainImage || swatch.getAttribute('data-main-image');
    const hoverImageUrl = swatch.dataset.hoverImage || swatch.getAttribute('data-hover-image');
    const productUrl = swatch.dataset.productUrl || swatch.getAttribute('data-product-url');

    // Re-query refs in case DOM was updated (e.g. after filter replaceInner)
    const mainImage = this.querySelector('[data-product-main-image]');
    const secondaryImage = this.querySelector('[data-product-secondary-image]');
    const productLink = this.querySelector('.product-card--images');
    this.swatches = this.querySelectorAll('[data-product-card-swatch]');
    this.wishListButton = this.querySelector('[data-product-wishlist-button]');

    // Active state
    this.swatches.forEach((s) => {
      s.classList.remove('active');
      s.setAttribute('aria-checked', 'false');
    });
    swatch.classList.add('active');
    swatch.setAttribute('aria-checked', 'true');

    if (this.wishListButton && productId) {
      this.wishListButton.setAttribute('data-product-id', productId);
    }

    // Main image — keep srcset/sizes so retina/PLP grid density is preserved after swatch change
    if (mainImage && mainImageUrl) {
      this.applyResponsiveImage(mainImage, mainImageUrl);
    }

    // Price
    if (this.price) {
      if (variantComparePrice && variantComparePrice !== variantPrice) {
        this.price.textContent = variantPrice;
        if (this.comparePrice) {
          this.comparePrice.textContent = variantComparePrice;
          this.comparePrice.classList.remove('hidden');
        }
      } else {
        if (this.comparePrice) this.comparePrice.classList.add('hidden');
        this.price.textContent = variantPrice || '';
      }
    }

    // Secondary/hover image
    const newHoverUrl = hoverImageUrl || mainImageUrl;
    if (secondaryImage) {
      secondaryImage.style.opacity = '';
      secondaryImage.setAttribute('alt', '');
      this.applyResponsiveImage(secondaryImage, newHoverUrl);
    } else if (newHoverUrl && productLink) {
      const img = document.createElement('img');
      img.className = 'secondary-image absolute w-full h-full object-cover top-0 block aspect-auto opacity-0 transition-opacity duration-200';
      img.setAttribute('alt', '');
      img.setAttribute('loading', 'lazy');
      img.setAttribute('data-product-secondary-image', '');
      const sizes = mainImage?.getAttribute('sizes') || ProductCardSwatches.PLP_IMAGE_SIZES;
      img.setAttribute('sizes', sizes);
      this.applyResponsiveImage(img, newHoverUrl);
      productLink.appendChild(img);
    }

    // Product link
    if (productUrl && productLink) {
      productLink.setAttribute('href', productUrl);
    }

    this.updatePromotionalLineFromSwatch(variantId);
    this.updateBadgesFromSwatch(variantId);
  }

  /**
   * Async safety-stock check — runs after render so it never blocks paint.
   * Reads variant-to-color data from data-inventory attr, fetches the Azure
   * inventory API, then marks swatches whose color is fully below safety stock.
   */
  async fetchAndApplySafetyStock() {
    const config = window.inventoryConfig;
    if (!config || !config.apiUrl || !config.apiKey) return;

    const rawJson = this.getAttribute('data-inventory');
    if (!rawJson) return;

    let data;
    try { data = JSON.parse(rawJson); } catch (_) { return; }
    if (!data || !data.products) return;

    const { apiUrl, apiKey, safetyStock = 2, skipSafetyStockTags = ['low_inventory'] } = config;
    const skipTags = skipSafetyStockTags.map(t => t.trim().toLowerCase()).filter(Boolean);
    /** Merchant tag: skip async safety-stock swatch overlays for this PLP row */
    const EXCLUDE_OVERLAY_TAG = 'safety-stock-exclude';

    const toVariantInt = (raw) => {
      const n = Number.parseInt(String(raw), 10);
      return Number.isFinite(n) ? n : null;
    };

    // Only request inventory for products that have at least one variant tracking inventory (matches PDP behavior)
    const productIdsTracked = Object.keys(data.products).filter((pid) =>
      (data.products[pid].variants || []).some((v) => v.tracked === true));

    const inventoryByProduct = {};
    await Promise.all(productIdsTracked.map(async (pid) => {
      try {
        const res = await fetch(`${apiUrl}/api/product/${pid}/inventory?code=${apiKey}&include_inventory=true`);
        if (res.ok) inventoryByProduct[pid] = await res.json();
      } catch (_) { /* Liquid fallback already rendered */ }
    }));

    for (const [pid, productData] of Object.entries(data.products)) {
      const apiItems = inventoryByProduct[pid];
      if (!Array.isArray(apiItems)) continue;

      if (!(productData.variants || []).some(v => v.tracked === true)) continue;

      const normalizedTags = (productData.tags || []).map((t) => String(t || '').trim().toLowerCase());
      if (normalizedTags.includes(EXCLUDE_OVERLAY_TAG)) continue;

      const hasSkipTag = normalizedTags.some(t => skipTags.includes(t));

      // Build variant availability map (mirrors PDP logic in product-options.js).
      // API may return variant_id strings; Liquid JSON uses numbers — always normalize for lookups.
      const available = {};
      for (const item of apiItems) {
        const vid = toVariantInt(item.variant_id);
        if (vid == null) continue;

        const def = productData.variants.find(v => toVariantInt(v.id) === vid);
        if (def?.policy === 'continue') { available[vid] = true; continue; }

        if (def && def.tracked === false) {
          available[vid] = !!def.available;
          continue;
        }

        const qty = typeof item.purchasable !== 'undefined'
          ? item.purchasable
          : Array.isArray(item.inventory_data)
            ? item.inventory_data.reduce((s, l) => l.fulfills_online_orders ? s + l.available : s, 0)
            : undefined;

        available[vid] = typeof qty !== 'undefined'
          ? parseInt(qty, 10) > (hasSkipTag ? 0 : safetyStock)
          : !!item.available;
      }

      // Group variants by color, then update swatches for fully sold-out colors
      const colorVariants = {};
      for (const v of productData.variants) {
        const nid = toVariantInt(v.id);
        if (nid == null) continue;
        (colorVariants[v.color] ||= []).push(nid);
      }

      for (const [color, variantIds] of Object.entries(colorVariants)) {
        if (!variantIds.every(id => available[id] === false)) continue;

        this.querySelectorAll(
          `[data-product-card-swatch][data-product-id="${pid}"][data-swatch-color="${color}"]`
        ).forEach(swatch => {
          swatch.classList.add('product-card__swatch--soldout', 'cursor-not-allowed');
          swatch.setAttribute('data-unavailable', '');
        });
      }
    }
  }

  init() {
    this.swatches.forEach(swatch => {
      swatch.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.handleSwatchClick(event);
      });
    });
  }

  handleSwatchClick(event) {
    const clickedSwatch = event.currentTarget;
    this.applySwatchVariant(clickedSwatch);
  }
}

// Register custom element
customElements.define('product-card-swatches', ProductCardSwatches);
})();
